

import { getPageTitle } from '~/assets/js/seo/title.js';
import { mapGetters } from 'vuex';
const environment = process.env.NODE_ENV || 'production';
const envSet = require(`~/api/env.${environment}.js`)

const notionFaqPageIds = {
	ja : '395c33b79cb380ddb3a7f38c5daea57d',
	en : '395c33b79cb380b98a0ae9b4906be0e9'
};

function getRichText(block) {
	const type = block.type;
	const data = block[type];

	if( data && data.rich_text ){
		return data.rich_text;
	}
	return [];
}

function getPlainText(block) {
	return getRichText(block).map((v) => {
		return v.plain_text;
	}).join('');
}

function escapeMarkdownText(text) {
	return text.replace(/\\/g, '\\\\').replace(/\[/g, '\\[').replace(/\]/g, '\\]');
}

function normalizeInternalHref(href) {
	const parsed = href.match(/^(https:\/\/garden-eight\.com)?([^?#]*)([?#].*)?$/);

	if( !parsed ){
		return href;
	}

	const origin = parsed[1] || '';
	const path = parsed[2];
	const state = parsed[3] || '';

	if( path === '' || path === '/' || path.charAt(0) !== '/' || path.charAt(path.length - 1) === '/' || /\.[a-zA-Z0-9]{2,5}$/.test(path) ){
		return href;
	}

	return origin + path + '/' + state;
}

function richTextToMarkdown(richText) {
	return richText.map((v) => {
		let text = escapeMarkdownText(v.plain_text);

		if( v.annotations.code ){
			text = `\`${text}\``;
		}
		if( v.annotations.bold ){
			text = `**${text}**`;
		}
		if( v.annotations.italic ){
			text = `_${text}_`;
		}
		if( v.annotations.strikethrough ){
			text = `~~${text}~~`;
		}
		if( v.annotations.underline ){
			text = `<u>${text}</u>`;
		}
		if( v.href ){
			text = `[${text}](${normalizeInternalHref(v.href)})`;
		}

		return text;
	}).join('');
}

function getMarkdownText(block) {
	return richTextToMarkdown(getRichText(block));
}

function escapeMarkdownTableCell(text) {
	return text.replace(/\|/g, '\\|').replace(/\n/g, '<br>');
}

function getTableMarkdown(block) {
	if( !block.children || !block.children.length ){
		return '';
	}

	const rows = block.children.filter((row) => {
		return row.type === 'table_row';
	}).map((row) => {
		return row.table_row.cells.map((cell) => {
			return escapeMarkdownTableCell(richTextToMarkdown(cell));
		});
	});

	if( !rows.length ){
		return '';
	}

	const header = block.table.has_column_header ? rows[0] : rows[0].map(() => {
		return '';
	});
	const divider = header.map(() => {
		return '---';
	});
	const body = block.table.has_column_header ? rows.slice(1) : rows;

	return [
		`| ${header.join(' | ')} |`,
		`| ${divider.join(' | ')} |`
	].concat(body.map((row) => {
		return `| ${row.join(' | ')} |`;
	})).join('\n');
}

function blocksToMarkdown(blocks) {
	const lines = [];
	let listType = null;

	blocks.forEach((block) => {
		const text = getMarkdownText(block);
		let nextListType = null;
		let line = '';

		switch( block.type ){
			case 'heading_1':
				line = `# ${text}`;
				break;
			case 'heading_2':
				line = `## ${text}`;
				break;
			case 'heading_3':
				line = `### ${text}`;
				break;
			case 'paragraph':
				line = text;
				break;
			case 'bulleted_list_item':
				nextListType = 'bulleted_list_item';
				line = `- ${text}`;
				break;
			case 'numbered_list_item':
				nextListType = 'numbered_list_item';
				line = `1. ${text}`;
				break;
			case 'table':
				line = getTableMarkdown(block);
				break;
			default:
				break;
		}

		if( !line ){
			return;
		}

		if( lines.length && listType !== nextListType ){
			lines.push('');
		}
		lines.push(line);
		listType = nextListType;
	});

	return lines.join('\n');
}

function blocksToMarkdownSections(blocks) {
	const sections = [];
	let currentSection = [];

	blocks.forEach((block) => {
		if( block.type === 'heading_2' && currentSection.length ){
			sections.push(currentSection);
			currentSection = [];
		}

		currentSection.push(block);
	});

	if( currentSection.length ){
		sections.push(currentSection);
	}

	return sections.map((sectionBlocks, index) => {
		return {
			key : `${index}-${sectionBlocks[0].id}`,
			markdown : blocksToMarkdown(sectionBlocks)
		};
	}).filter((section) => {
		return section.markdown;
	});
}

function blocksToFaqItems(blocks) {
	const items = [];
	let currentItem = null;

	blocks.forEach((block) => {
		if( block.type === 'heading_2' ){
			currentItem = null;
			return;
		}

		if( block.type === 'heading_3' ){
			currentItem = {
				question : getPlainText(block),
				answer : []
			};
			items.push(currentItem);
			return;
		}

		if( !currentItem ){
			return;
		}

		if( block.type === 'divider' ){
			return;
		}

		const text = block.type === 'table' ? getTableMarkdown(block) : getPlainText(block);

		if( text ){
			currentItem.answer.push(text);
		}
	});

	return items.filter((item) => {
		return item.question && item.answer.length;
	}).map((item) => {
		return {
			question : item.question,
			answer : item.answer.join('\n')
		};
	});
}

function replaceTextPlain(message, locale, replaceText) {
	const aText = replaceText.a;
	const bText = replaceText.b;

	return message.replace(/\{\{(.*?)\}\}/g, (match, p1) => {
		if (bText[locale] && bText[locale].hasOwnProperty(p1)) {
			return p1.split(' ').map((word) => {
				return word.charAt(0) + word.slice(1).toLowerCase();
			}).join(' ');
		}
		if (aText.hasOwnProperty(p1)) {
			return p1;
		}
		return p1;
	});
}

function buildFaqJsonLd(items, locale, replaceText) {
	const mainEntity = items.map((item) => {
		return {
			'@type': 'Question',
			name: item.question,
			acceptedAnswer: {
				'@type': 'Answer',
				text: replaceTextPlain(item.answer, locale, replaceText)
			}
		};
	});

	if (!mainEntity.length) {
		return null;
	}

	return {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		inLanguage: locale === 'ja' ? 'ja-JP' : 'en-US',
		mainEntity
	};
}

export default {

	data() {
		return {
			model: [
				{ src: '/assets/models/blender/particle/nuts-half.glb', scale: 0.8 },
				{ src: '/assets/models/blender/particle/nuts-half.glb', scale: 0.8 },
				{ src: '/assets/models/blender/particle/nuts-half.glb', scale: 0.8 }
			],
			faqPages : {
				ja : [],
				en : []
			},
			error: null
		};
	},

	computed: {
		...mapGetters([
			'chatAi'
		]),
		faqBlocks() {
			return this.faqPages[this.$i18n.locale];
		},
		faqMarkdownSections() {
			return blocksToMarkdownSections(this.faqBlocks);
		},
		faqItems() {
			return blocksToFaqItems(this.faqBlocks);
		}
	},

	methods: {
		formatFaqMarkdown(text) {
			return this.$md.render( this.replaceText(text) );
		},
		replaceText(message) {
			const aText = this.chatAi.data.replaceText.a;
			const bText = this.chatAi.data.replaceText.b;
			const locale = this.$i18n.locale;

			return message.replace(/{{(.*?)}}/g, (match, p1) => {
				if (bText[locale] && bText[locale].hasOwnProperty(p1)) {
					return bText[locale][p1];
				}
				if (aText.hasOwnProperty(p1)) {
					return aText[p1];
				}
				return p1;
			});
		}
	},

	head() {
		let _title = getPageTitle(this.$i18n.locale, 'FAQ');
		let _description = 'Frequently Asked Questions';
		const locale = this.$i18n.locale;
		const faqJsonLd = buildFaqJsonLd(
			this.faqItems,
			locale,
			this.chatAi.data.replaceText
		);
		const head = {
			title: _title,
			meta: [
				{
					name: 'description',
					content: _description,
				}
			]
		};

		if (faqJsonLd) {
			head.script = [
				{
					hid: 'faq-jsonld',
					type: 'application/ld+json',
					json: faqJsonLd
				}
			];
		}

		return head;
	},

	async fetch ({ store, $http }) {
		if( !store.getters.projects.length ){
			const request = `${envSet.staticUrl}/db/projects.json`;
			const response = await $http.$get( request );
			store.dispatch( 'setProjects', response );
		}
	},

	async asyncData() {
		const faqEntries = await Promise.all(Object.keys(notionFaqPageIds).map(async (locale) => {
			const request = `${envSet.api.notion_page}?page_id=${notionFaqPageIds[locale]}&_=${new Date().getTime()}`;
			const response = await fetch( request, {
				cache: 'no-store'
			});

			if( !response.ok ){
				throw new Error('Network response was not ok');
			}

			const data = await response.json();

			if( data.error ){
				throw new Error(data.error);
			}

			return [locale, data.results];
		}));

		return {
			faqPages : faqEntries.reduce((pages, entry) => {
				pages[entry[0]] = entry[1];
				return pages;
			}, {})
		};
	},

	mounted(){
		ROUTE.onMounted( this.$store.$device, {
			current : { type : 'subpage', slug : 'subpage' },
			prev : null,
			next : null
		});
		this.$nextTick(() => {
			if( styleInview ) styleInview.onInit();
		});
	},

	transition : {
		mode: 'out-in',
		css: false,
		beforeEnter( el ){
			ROUTE.beforeEnter( el );
		},
		enter( el, done ){
			ROUTE.enter( el, done );
		},
		afterEnter( el ){
			ROUTE.afterEnter( el );
		},
		leave( el, done ){
			ROUTE.leave(el, done);
		},
	}

}
